import {
  Component,
  HostListener,
  Inject,
  Input,
  LOCALE_ID,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  Articles,
  ExtendedProperties,
  IContact, LeadComposerChangesModel,
  LeadData,
  LeadsHistory
} from '../../../../models/leads/leads.model';
import { PhoneNumberHelper } from '../../../../helper/phone-number.helper';
import { LeadHelper } from '../../../../helper/lead.helper';
import { TitleHelper } from '../../../../helper/title.helper';
import { DateTimeHelper } from '../../../../helper/date-time.helper';
import { HistoryCommentComponent } from '../comment/comment.component';
import { NotInterestedTypes } from '../../../../models/action.model';
import { TextHelper } from '../../../../helper/text.helper';
import { formatCurrency, getCurrencySymbol } from '@angular/common';
import { TranslationDirective } from '../../../shared-module/directives/translation.directive';
import { UtmParamsHelper } from '../../../../helper/utm-params.helper';


@Component({
  selector: 'fi-print-details',
  templateUrl: './print-details.component.html',
  styleUrls: ['./print-details.component.scss'],
  standalone: false,
})

export class PrintDetailsComponent implements OnChanges {
  @Input() selectedLead: LeadData;
  @Input() selectedLeadHistory: LeadsHistory[];
  @Input() selectedContact: IContact;
  @Input() addressToDisplay: string;
  @Input() extendedProperties: ExtendedProperties;
  @Input() mainOffer: LeadComposerChangesModel;
  @Input() mainOrder: LeadComposerChangesModel;
  @Input() offerTotal: number;
  @Input() orderTotal: number;
  @Input() composerOrders: LeadComposerChangesModel[];
  @Input() composerOffers: LeadComposerChangesModel[];
  @Input() consentAccredation: boolean;
  @Input() articles: Articles[];
  @Input() isOffer: boolean;
  @Input() consultationTypeName: string;
  @Input() isAdminUser: boolean;
  @Input() isPlanerUser: boolean;

  printedOn = new Date();
  hasAlternative = false;
  articleColumnsWidthWidth = {
    width: '110px',
    maxWidth: '110px',
  };

  private readonly historyContentMap = new WeakMap<LeadsHistory, { firstLine: string; otherLines: string[] }[]>();
  readonly leadHelper = LeadHelper;
  readonly titleHelper = TitleHelper;
  readonly dateTimeHelper = DateTimeHelper;

  readonly CampaignKey = UtmParamsHelper.CampaignKey;
  readonly KKZKey = UtmParamsHelper.KKZKey;
  readonly SourceKey = UtmParamsHelper.SourceKey;
  readonly MediumKey = UtmParamsHelper.MediumKey;

  constructor(
    @Inject(LOCALE_ID) public locale: string
  ) {

  }


  @HostListener('window:beforeprint', ['$event'])
  onBeforePrint( event: Event ): void {
    this.updatePrintedAtDate();
  }

  existsOfferSubmitAndIsExpired( composerOffer: LeadComposerChangesModel ): boolean {
    return this.selectedLeadHistory?.some(item => item.ComposerChanges.some(changes => (composerOffer.Name === changes.Name) && (composerOffer.IsHandedOver)));
  }

  ngOnChanges( changes: SimpleChanges ): void {
    this.hasAlternative = !!this.composerOffers?.some(x => !x.IsMain);

    if ( changes.articles && this.articles ) {
      let width = 11 * this.articles.reduce(( acc, curr ) => Math.max(acc, curr.Article?.length ?? 0), 0) + 10;
      width = Math.max(110, width);

      this.articleColumnsWidthWidth = {
        width: `${width}px`,
        maxWidth: `${width}px`,
      };
    }
  }

  private updatePrintedAtDate(): void {
    this.printedOn = new Date();
  }

  get contactPhone(): string {
    return PhoneNumberHelper
      .getPhoneNumber(this.selectedContact?.PhonePrefix, this.selectedContact?.Phone);
  }

  get contactMobile(): string {
    return PhoneNumberHelper
      .getPhoneNumber(this.selectedContact?.MobilePrefix, this.selectedContact?.Mobile);
  }

  get contactAdditionalMobile(): string {
    return PhoneNumberHelper
      .getPhoneNumber(this.selectedContact?.PhoneOtherPrefix, this.selectedContact?.PhoneOther);
  }

  getConstructionIconUrl( typeId: number, isUrl: boolean ): string {
    return LeadHelper.getConstructionIconUrl(typeId, isUrl);
  }

  getHistoryContent( item: LeadsHistory ): { firstLine: string; otherLines: string[] }[] {
    let result = this.historyContentMap.get(item);

    if ( !result ) {
      const date = this.dateTimeHelper.getFormattedDate(item?.AppointmentDate ?? item?.CreTs);
      result = [];
      this.historyContentMap.set(item, result);
      const firstLine = `${item.Creator?.FirstName} ${item.Creator?.LastName} - ${item?.Creator?.Organisation} %REQUEST_DETAIL_HISTORY_LEAD_CREATE_DATE% ${item?.LeadNumber ?? '--'}`;
      let comment = item?.Comment ?? item?.LeadText ?? null;
      const actionText = item?.ActionText ?? null;
      let notInterestedType: NotInterestedTypes;
      let text = '';


      result.push({
        firstLine,
        otherLines: [],
      })

      if ( item?.NotInterestedTypeId && HistoryCommentComponent.notInterestedTypes?.length ) {
        notInterestedType = HistoryCommentComponent
          .notInterestedTypes.find(x => x.Id === item.NotInterestedTypeId) ?? null;
      }

      switch ( item?.ActionName ) {
        case 'APPOINTMENT_CREATE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_APPOINTMENT_CREATE% ${date}`);
          break;
        case 'APPOINTMENT_CREATE_EXTERNAL':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_APPOINTMENT_CREATE_EXTERNAL% ${date}`);
          break;
        case 'APPOINTMENT_MOVE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_APPOINTMENT_MOVE% ${date}`);
          break;
        case 'LEAD_REMINDER':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_REMINDER% ${date}`);
          break;
        case 'APPOINTMENT_REMINDER':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_APPOINTMENT_REMINDER% ${date}`);
          break;
        case 'COMMENT_CREATE':
          break;
        case 'COMPOSER_IMPORT':
        case 'COMPOSER_MANUAL':
          if ( item?.ActionName === 'COMPOSER_IMPORT' ) {
            result[0].otherLines.push(`Composer Import ${date}`);
          }

          if ( item.ComposerChanges?.length ) {
            let lastChange: LeadComposerChangesModel = null;

            for ( const change of item.ComposerChanges ) {
              text = '';

              const addComposerLineIfNecessary = ( line: string ) => {
                if (
                  lastChange?.Operation !== change.Operation
                ) {
                  result[0].otherLines.push(line);
                  lastChange = change;
                }
              }

              switch ( change.Operation ) {
                case 'C':
                  if ( !change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      if ( change.IsMain ) {
                        addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_OFFER_ADDED%:`);
                      } else {
                        addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_ALTERNATIVE_ADDED%`);
                      }
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      if ( change.IsMain ) {
                        addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_LINKS%`);
                      } else {
                        addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_LINKS%`);
                      }
                    }
                  }

                  if ( change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_ORDER_ADDED%`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_LINKS%`);
                    }
                  }

                  break;
                case 'U':
                  if ( !change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_OFFER_CHANGED%:`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_OFFER_CHANGED%:`);
                    }
                  }

                  if ( change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_ORDER_CHANGED%:`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_ORDER_CHANGED%:`);
                    }
                  }
                  break;
                case 'D':
                  if ( !change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_OFFER_DELETED%:`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_OFFER_DELETED%:`);

                    }
                  }

                  if ( change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_ORDER_DELETED%:`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_ORDER_DELETED%:`);
                    }
                  }
                  break;
                case 'M':
                  if ( !change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_MAIN_OFFER_SWITCH%`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_ALTERNATIVE_BECOMES_OFFER%`);
                    }
                  }

                  if ( change.IsOrder ) {
                    if ( item.ActionName === 'COMPOSER_MANUAL' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_ORDER_DELETED%:`);
                    }

                    if ( item.ActionName === 'COMPOSER_IMPORT' ) {
                      addComposerLineIfNecessary(`%REQUEST_DETAIL_LEAD_HISTORY_MANUAL_ORDER_CHANGED%:`);
                    }
                  }
                  break;
              }

              if ( change.Operation === 'C' && item.ActionName === 'COMPOSER_IMPORT' ) {
                if ( !change.IsOrder ) {
                  if ( change.IsMain ) {
                    result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_OFFER_CREATED%:`);
                  } else {
                    result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_ALTERNATIVE_ADDED%:`);
                  }
                }

                if ( change.IsOrder ) {
                  result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_COMPOSER_ORDER_CREATED%:`);
                }
              }

              text += `${change.Name} `;

              if ( change.IsMain && change.Operation === 'M' ) {
                text += `%REQUEST_DETAIL_LEAD_HISTORY_STATUS_MAIN_OFFER_SWITCH_OFFER% `;
              }

              if ( !change.IsMain && change.Operation === 'M' ) {
                text += `%REQUEST_DETAIL_LEAD_HISTORY_STATUS_MAIN_OFFER_SWITCH_ALTERNATIVE% `;
              }

              if ( change.Operation !== 'M' ) {
                text += formatCurrency(change.Amount, this.locale, getCurrencySymbol(change.CurrencyCode, 'narrow'));
              }

              result[0].otherLines.push(text);
            }
          }
          break;
        case 'DEADLINE_SERVICE':
          result[0].otherLines.push(`Per E-Mail für Angebotsübergabe vereinbart am ${item.Deadline}`);
          break;
        case 'LEAD_CREATE':
          const consentMarketing = item?.ConsentMarketing;
          const consentThirdParties = item?.ConsentThirdParties;
          const leadSerialNumber = this.selectedLead?.Lead.SerialNumber;
          const leadInvoiceNumber = this.selectedLead?.Lead.InvoiceNumber;
          const leadSourceText = this.selectedLead?.Lead.LeadSourceText;
          const sourceDomain = this.selectedLead?.Lead.SourceDomain ?? '';
          const UTMParameters = this.selectedLead?.Lead.UTMParameters;
          const referrerUrl = this.selectedLead?.Lead.ReferrerUrl;
          const privacyDocument = this.selectedLead?.PrivacyDocument;
          const eventsText = this.selectedLead?.Events?.map(item => item.Location).join(', ');
          const events = this.selectedLead?.EventAttendees;

          const map = UtmParamsHelper.extractSourceDomainToUtmParams(UTMParameters, this.isAdminUser, this.isPlanerUser);
          const sourceDomainUTMParams = Array.from(map.entries());
          const hasCampaign = map.has(this.CampaignKey) && (map.get(this.CampaignKey) || "").trim().length > 0;
          const displaySourceDomain = hasCampaign ? UtmParamsHelper.stripQuery(sourceDomain) : sourceDomain;

          const additionalCreationLines = [`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_PRIVACY_DOCUMENTS% ${this.dateTimeHelper.getFormattedDate(item.CreTs)}`];

          if ( privacyDocument ) {
            additionalCreationLines.push(TranslationDirective.escapeKeys(privacyDocument.Name))
          }


          result[0].otherLines.push(
            `%REQUEST_DETAIL_HISTORY_LEAD_CREATE_SECOND_ROW% ${comment}`,
          )


          if ( eventsText ) {
            result[0].otherLines.push(
              TranslationDirective.escapeKeys(eventsText),
              `%REQUEST_DETAIL_HISTORY_CONSENT_ACCREDITATION% ${this.consentAccredation ? 'REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1' : 'REQUEST_CREATE_CONTACT_CHECKBOX_DISAGREE'}`,
            )
          }

          if ( leadSerialNumber ) {
            result[0].otherLines.push(
              `%REQUEST_DETAIL_HISTORY_PRODUCT_NUMBER% ${leadSerialNumber}`,
            )
          }

          if ( leadInvoiceNumber ) {
            result[0].otherLines.push(
              `%REQUEST_DETAIL_HISTORY_INVOICE_NUMBER% ${leadInvoiceNumber}`,
            )
          }

          if ( events?.length ) {
            result[0].otherLines.push(
              `%REQUEST_DETAIL_HISTORY_LEAD_CREATE_EVENT_PARTICIPANTS%`,
            )

            for ( const event of events ) {
              result[0].otherLines.push(
                `${event.FirstName} ${event.LastName} ${event.Email}`,
              )
            }
          }

          result[0].otherLines.push(
            `%REQUEST_DETAIL_HISTORY_LEAD_CREATE_LEAD_TYPE% ${leadSourceText}`,
          )

          if ( referrerUrl ) {
            result[0].otherLines.push(`Referrer URL: ${referrerUrl}`);
          }

          if ( sourceDomain ) {
            result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_SOURCE_DOMAIN%: ${displaySourceDomain}`);
          }

          if ( sourceDomainUTMParams && sourceDomainUTMParams.length > 0 ) {
            let addTitle = true;
            for ( const item in sourceDomainUTMParams ) {
              const value = sourceDomainUTMParams[item][1];
              const key = sourceDomainUTMParams[item][0];

              if ( addTitle ) {
                result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_UTM_PARAMETERS%`);
                addTitle = false;
              }

              if (value) {
                if (key === this.CampaignKey) {
                  result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_CAMPAIGN% ${value}`);
                } else if (key === this.KKZKey) {
                  result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_KKZ% ${value}`);
                } else if (key === this.SourceKey) {
                  result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_SOURCE% ${value}`);
                } else if (key === this.MediumKey) {
                  result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_CREATE_MEDIUM% ${value}`);
                } else if ( key !== this.CampaignKey && key !== this.KKZKey && key !== this.SourceKey && key !== this.MediumKey ) {
                  result[0].otherLines.push(`${key}: ${value}`);
                }
              }
            }
          }

          if ( item.Documents?.length ) {
            result[0].otherLines.push(
              `%REQUEST_DETAIL_HISTORY_UPLOAD_FILE% ${item.Documents.map(doc => TranslationDirective.escapeKeys(doc.Name)).join(', ')}`,
            );
          }


          result.push({
            firstLine,
            otherLines: []
          })

          if ( consentThirdParties ) {
            result[1].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_THIRD_PARTIES_AGREE%')
          } else {
            result[1].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_DISAGREE% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_THIRD_PARTIES_DISAGREE%')
          }

          result[1].otherLines.push(...additionalCreationLines);

          result.push({
            firstLine,
            otherLines: []
          })

          if ( consentMarketing ) {
            result[2].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_AGREE%')
          } else {
            result[2].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_DISAGREE% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_DISAGREE%')
          }

          result[2].otherLines.push(...additionalCreationLines);

          result.push({
            firstLine,
            otherLines: [`%REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1% %REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_2%`]
          })

          result[3].otherLines.push(...additionalCreationLines);

          return result;
        case 'LEAD_FORWARD':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_LEAD_FORWARD% ${item.LeadEditor?.Organisation} - ${item?.LeadEditor?.FirstName + ' ' + item?.LeadEditor?.LastName}`);
          break;
        case 'LEAD_FORWARD_CONFIRM':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_TAKEOVER% ${item.LeadEditor?.FirstName} ${item.LeadEditor?.LastName}`);
          break;
        case 'LEAD_FORWARD_EXT':
          result[0].otherLines.push(`%REQUEST_DETAIL_HISTORY_LEAD_EXTERNAL_USER% ${item.ExternalEmail}`);
          break;
        case 'LEAD_FORWARD_UNDO':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_FORWARD_UNDO% ${item.LeadEditor?.Organisation} - ${item?.LeadEditor?.FirstName + ' ' + item?.LeadEditor?.LastName}`);
          break;
        case 'LEAD_NOT_INTERESTED':
          if ( notInterestedType ) {
            if ( notInterestedType.IsFinstral ) {
              text += ` ${item?.LeadEditor?.FirstName ? item?.LeadEditor?.FirstName + ' ' + item?.LeadEditor?.LastName : ''}`
            }

            if ( !notInterestedType.IsFinstral && !this.selectedContact?.Anonymized ) {
              text += ` ${this.selectedContact.FirstName} ${this.selectedContact.LastName}`
            }

            if ( !notInterestedType.IsFinstral && this.selectedContact?.Anonymized ) {
              text += ` %REQUEST_DETAIL_HEADLINE_CONTACT_ANONYMIZE%`
            }
          }

          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_NOT_INTERESTED%${text}`);
          break;
        case 'LEAD_PROCESS':
        case 'LEAD_PROCESS_EXT':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_LEAD_PROCESS% ${item?.ExternalEmail}`);
          break;
        case 'LEAD_REJECT':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_REJECT% ${item.LeadEditor?.Organisation} - ${item?.LeadEditor?.FirstName + ' ' + item?.LeadEditor?.LastName}`);
          break;
        case 'LEAD_REMINDER_EXT':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_REMINDER_EXT% ${item?.ExternalEmail}`);
          break;
        case 'LEAD_REOPEN':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_LEAD_REOPEN% ${date}`);

          if ( comment ) {
            result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_REJECT_NOTE%`);
          }
          break;
        case 'LEAD_STATUS':
          if ( actionText ) {
            result[result.length - 1].otherLines.push(actionText);
          }
          break;
        case 'LEAD_TAKEOVER':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_TAKEOVER% ${item?.LeadEditor.FirstName + ' ' + item?.LeadEditor.LastName}`);
          break;
        case 'LEAD_TYPE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_LEAD_TYPE% ${item?.LeadTypeText}`);
          break;
        case 'NEGOTIATION_CREATE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_NEGOTIATION_CREATE% ${date}`);
          break;
        case 'NEGOTIATION_MOVE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_NEGOTIATION_MOVE% ${date}`);
          break;
        case 'OFFER_DELAY':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_OFFER_SUBMIT% ${date}`);
          break;
        case 'OFFER_MOVE':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_OFFER_DELAY% ${date}`);
          break;
        case 'OFFER_SUBMIT':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_OFFER_MOVE% ${date}`);
          break;
        case 'OFFER_SUBMIT_EXTERNAL':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_OFFER_SUBMIT_EXTERNAL% ${date}`);
          break;
        case 'ORDER_PLACED':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_ORDER_PLACED% ${date}`);
          break;
        case 'ORDER_PLACED_EXTERNAL':
          result[0].otherLines.push(`%REQUEST_DETAIL_LEAD_HISTORY_STATUS_ORDER_PLACED_EXTERNAL% ${date}`);
          break;
        case 'PRIVACY_CHANGE':
          if ( item.ConsentMarketing ) {
            result[0].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_AGREE%')
          } else {
            result[0].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_DISAGREE% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_DISAGREE%')
          }

          if ( item.ConsentThirdParties ) {
            result[0].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_PRIVACY_AGREE_TO_STORE_DATA_1% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_THIRD_PARTIES_AGREE%')
          } else {
            result[0].otherLines.push('%REQUEST_CREATE_CONTACT_CHECKBOX_DISAGREE% %REQUEST_CREATE_CONTACT_CHECKBOX_MARKETING_CONSENT_THIRD_PARTIES_DISAGREE%')
          }

          break;
      }

      if ( comment ) {
        result[result.length - 1].otherLines.push(TextHelper.getInnerText(TranslationDirective.escapeKeys(comment)));
      }

      if ( item.Documents?.length ) {
        result[0].otherLines.push(
          `${item.Documents.map(doc => TranslationDirective.escapeKeys(doc.Name)).join(', ')}`,
        );
      }
    }

    return result;
  }
}
